/**
 * Payment parameters of a bank return, kept out of every report and log.
 *
 * Plain JavaScript on purpose: `server.js` runs as is under Node, outside
 * Vite, and imports this module directly, while the browser bundle reaches it
 * through `src/services/telemetry/sanitize.ts`. One list of parameters for
 * both sides means a parameter added here is dropped from Sentry events of
 * both, from server breadcrumbs and from the access log alike. The Dockerfile
 * copies this directory next to `server.js`.
 */

/** One-time 3DS results, redacted whatever their value. */
export const PAYMENT_RESULT_KEYS = [
  'redirectresult',
  'threedsresult',
  'threeds2result',
];

/**
 * Query parameters of a bank return: the one-time 3DS results plus the
 * legacy 3DS1 fields a bank may append. Matched case-insensitively.
 */
const PAYMENT_QUERY_KEYS = new Set([
  ...PAYMENT_RESULT_KEYS,
  'payload',
  'md',
  'pares',
]);

/** Request headers that carry the URL of the page that sent the request. */
const REFERRER_HEADERS = new Set(['referer', 'referrer']);

/** Breadcrumb fields that hold a URL: `from`/`to` of a navigation, `url` of a request. */
const BREADCRUMB_URL_KEYS = ['from', 'to', 'url'];

/**
 * Span attributes that hold a request URL with its query: the old and the
 * stable HTTP semantic conventions of `@opentelemetry/instrumentation-http`
 * (`http.url`, `http.target`, `url.full`), which `@sentry/node` copies into
 * `contexts.trace.data` of a transaction and `data` of each of its spans.
 */
const SPAN_URL_KEYS = ['http.url', 'http.target', 'url.full'];

/**
 * Span attributes that hold the query alone: `url.query` without the leading
 * `?`, `http.query` (set by the Sentry fetch instrumentation) with it.
 */
const SPAN_QUERY_KEYS = ['url.query', 'http.query'];

/**
 * @param {string} key
 * @returns {string}
 */
const decodeQueryKey = (key) => {
  try {
    return decodeURIComponent(key.replace(/\+/g, ' '));
  } catch {
    return key;
  }
};

/**
 * Whether a query key, raw or encoded, is a payment parameter.
 *
 * @param {string} key
 * @returns {boolean}
 */
export const isPaymentQueryKey = (key) =>
  PAYMENT_QUERY_KEYS.has(decodeQueryKey(key).toLowerCase());

/**
 * Drops payment parameters from a URL, absolute or relative, and keeps the
 * rest of it as is.
 *
 * Unlike `sanitizeUrl` this keeps the query: the page URL of an event and the
 * `from`/`to` of a navigation breadcrumb are worth reading in full, but the
 * bank's return to the membership checkout puts a one-time `redirectResult`
 * there, and that must never reach a report or a log.
 *
 * @param {string} raw
 * @returns {string}
 */
export const stripPaymentParams = (raw) => {
  const hashIndex = raw.indexOf('#');
  const beforeHash = hashIndex === -1 ? raw : raw.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : raw.slice(hashIndex);
  const queryIndex = beforeHash.indexOf('?');
  if (queryIndex === -1) {
    return raw;
  }
  const kept = beforeHash
    .slice(queryIndex + 1)
    .split('&')
    .filter((pair) => pair && !isPaymentQueryKey(pair.split('=')[0] ?? ''));
  const base = beforeHash.slice(0, queryIndex);
  return `${kept.length > 0 ? `${base}?${kept.join('&')}` : base}${hash}`;
};

/**
 * Drops payment parameters from the `query_string` of a Sentry request, in
 * any of the shapes the SDKs send: a string, `[key, value]` pairs or an object.
 *
 * @param {unknown} query
 * @returns {unknown}
 */
const stripPaymentQuery = (query) => {
  if (typeof query === 'string') {
    return stripPaymentParams(`?${query.replace(/^\?/, '')}`).replace(
      /^\?/,
      '',
    );
  }
  if (Array.isArray(query)) {
    return query.filter(
      (entry) => !(Array.isArray(entry) && isPaymentQueryKey(String(entry[0]))),
    );
  }
  if (query && typeof query === 'object') {
    return Object.fromEntries(
      Object.entries(query).filter(([key]) => !isPaymentQueryKey(key)),
    );
  }
  return query;
};

/**
 * Removes payment query parameters from the URL and query attributes of a
 * span or trace context, in place; keeps a leading `?` of a query as it was.
 *
 * @param {unknown} data
 */
const scrubSpanData = (data) => {
  if (!data || typeof data !== 'object') {
    return;
  }
  const attributes = /** @type {{ [key: string]: unknown }} */ (data);
  for (const key of SPAN_URL_KEYS) {
    const value = attributes[key];
    if (typeof value === 'string') {
      attributes[key] = stripPaymentParams(value);
    }
  }
  for (const key of SPAN_QUERY_KEYS) {
    const value = attributes[key];
    if (typeof value === 'string') {
      attributes[key] = value.startsWith('?')
        ? stripPaymentParams(value)
        : stripPaymentQuery(value);
    }
  }
};

/**
 * Removes payment query parameters from the URLs of a breadcrumb, in place.
 *
 * @template {{ data?: { [key: string]: unknown } }} T
 * @param {T} breadcrumb
 * @returns {T}
 */
export const scrubPaymentBreadcrumb = (breadcrumb) => {
  const { data } = breadcrumb;
  if (!data) {
    return breadcrumb;
  }
  for (const key of BREADCRUMB_URL_KEYS) {
    const value = data[key];
    if (typeof value === 'string') {
      data[key] = stripPaymentParams(value);
    }
  }
  return breadcrumb;
};

/**
 * Removes payment query parameters from everything in an event that carries
 * the page URL — the request with its referrer header, the transaction name,
 * the URL attributes of the trace context and of every span with the span
 * descriptions, and the breadcrumbs — in place. Runs on every event and
 * transaction of the browser and of the SSR server, so a bank return can
 * never leave its `redirectResult` in Sentry, whoever reports from that page.
 *
 * @template {import('./paymentParams').UrlCarryingEvent} T
 * @param {T} event
 * @returns {T}
 */
export const scrubPaymentEvent = (event) => {
  if (event.transaction) {
    event.transaction = stripPaymentParams(event.transaction);
  }
  if (event.request) {
    if (event.request.url) {
      event.request.url = stripPaymentParams(event.request.url);
    }
    if (event.request.query_string !== undefined) {
      event.request.query_string = stripPaymentQuery(
        event.request.query_string,
      );
    }
    const { headers } = event.request;
    if (headers) {
      for (const key of Object.keys(headers)) {
        const value = headers[key];
        if (
          REFERRER_HEADERS.has(key.toLowerCase()) &&
          typeof value === 'string'
        ) {
          headers[key] = stripPaymentParams(value);
        }
      }
    }
  }
  scrubSpanData(event.contexts?.trace?.data);
  if (Array.isArray(event.spans)) {
    for (const span of event.spans) {
      if (!span || typeof span !== 'object') {
        continue;
      }
      scrubSpanData(span.data);
      if (typeof span.description === 'string') {
        span.description = stripPaymentParams(span.description);
      }
    }
  }
  event.breadcrumbs?.forEach(scrubPaymentBreadcrumb);
  return event;
};

/**
 * Request URL of an access log line without payment parameters.
 *
 * @param {{ originalUrl?: string; url?: string }} req
 * @returns {string}
 */
export const safeRequestUrl = (req) =>
  stripPaymentParams(req.originalUrl || req.url || '');

/**
 * Referrer of an access log line without payment parameters, `-` without
 * one: every request a bank return page makes sends its URL as the referrer.
 *
 * @param {{ headers: { [key: string]: string | string[] | undefined } }} req
 * @returns {string}
 */
export const safeReferrer = (req) => {
  const referrer = req.headers.referer || req.headers.referrer;
  return typeof referrer === 'string' ? stripPaymentParams(referrer) : '-';
};
